import { lazy, Suspense, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import FlightWorkspace from './app/FlightWorkspace';
import { AppShell } from './app/AppShell';
import { EmptyState } from './app/EmptyState';
import { DEFAULT_TAB, type TabKey } from './app/tabs';
import { useT } from './i18n';
import Auth from './routes/Login';
import Flight from './routes/Flight';
import Flights from './routes/Flights';
import { Forgot, Reset } from './routes/Password';
import Settings from './routes/Settings';
import Shared from './routes/Shared';
import { useSession } from './session';

/**
 * Split out of the eager bundle: `/aircraft` is the only importer of AIRCRAFT_PRESETS, and
 * `data/aircraft-presets.json` is 168 kB that sign-in, the flight list and the shared
 * read-only view would otherwise all download to reach step 1 of "add an aircraft". Same
 * pattern as the map (`MapView`), and the page is behind a sign-in, so the extra request
 * is free.
 */
const AircraftPage = lazy(() => import('./routes/Aircraft'));

/**
 * `/sources` is prose and links only, read once and rarely: it has no business
 * in the bundle the workspace waits for.
 */
const SourcesPage = lazy(() => import('./routes/Sources'));

function AircraftRoute() {
  const t = useT();
  return (
    <Suspense fallback={<div className="ae-page">{t('loading')}</div>}>
      <AircraftPage />
    </Suspense>
  );
}

function SourcesRoute() {
  const t = useT();
  return (
    <Suspense fallback={<div className="ae-page">{t('loading')}</div>}>
      <SourcesPage />
    </Suspense>
  );
}

function SharedRoute() {
  const { token } = useParams<{ token: string }>();
  if (!token) return <Navigate to="/" replace />;
  return <Shared token={token} />;
}

/**
 * `/` is a local, unsaved flight when signed out — the single-file predecessor's
 * whole behaviour, kept — and the flight list when signed in. The tab is state
 * rather than a route here, because a flight with no id has no address to hang
 * it on.
 */
function Home() {
  const session = useSession();
  const t = useT();
  const [tab, setTab] = useState<TabKey>(DEFAULT_TAB);
  if (session.loading) return <div className="ae-page">{t('loading')}</div>;
  if (session.user) return <Navigate to="/flights" replace />;
  return <FlightWorkspace tab={tab} onTab={setTab} />;
}

/** `/plans/:id` → `/flights/:id`: links and bookmarks from the previous shell. */
function PlanRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? '/flights/' + id : '/flights'} replace />;
}

function NotFound() {
  const t = useT();
  const navigate = useNavigate();
  return (
    <AppShell>
      <div className="ae-page">
        <EmptyState
          title={t('app.notfound')}
          action={
            <button type="button" className="ae-btn ae-btn-primary" onClick={() => navigate('/flights')}>
              {t('app.toflights')}
            </button>
          }
        />
      </div>
    </AppShell>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      {/* the address the reset mail links to — `{PUBLIC_URL}/reset?token=…` */}
      <Route path="/forgot" element={<Forgot />} />
      <Route path="/reset" element={<Reset />} />
      <Route path="/flights" element={<Flights />} />
      <Route path="/flights/:id" element={<Flight />} />
      <Route path="/flights/:id/:tab" element={<Flight />} />
      <Route path="/aircraft" element={<AircraftRoute />} />
      <Route path="/settings" element={<Settings />} />
      {/* every ⓘ and the rail's quiet link end here — the attributions in full */}
      <Route path="/sources" element={<SourcesRoute />} />
      <Route path="/s/:token" element={<SharedRoute />} />
      {/* the previous shell's addresses */}
      <Route path="/plans" element={<Navigate to="/flights" replace />} />
      <Route path="/plans/:id" element={<PlanRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
